import type { AgentRequest } from "../../shared/types/AgentRequest";
import type { TldrawAgent } from "../agent/TldrawAgent";
import type {
  AgentModeDefinition,
  AgentModeType,
} from "./AgentModeDefinitions";

/**
 * Lifecycle hooks for an agent mode.
 * Each mode can optionally implement these hooks to respond to state changes.
 */
export interface AgentModeNode {
  onEnter?(agent: TldrawAgent, fromMode: AgentModeType): void;
  onExit?(agent: TldrawAgent, toMode: AgentModeType): void;
  onPromptCancel?(agent: TldrawAgent, request: AgentRequest): void;
}

/**
 * Lifecycle implementations for each agent mode.
 *
 * This chart maps mode types to their lifecycle hooks.
 * Modes can implement any subset of hooks (all are optional).
 * Not all modes need an entry - modes without entries simply have no lifecycle behavior.
 *
 * To add lifecycle behavior for a new mode:
 * 1. Add the mode to AGENT_MODE_DEFINITIONS in AgentModeDefinitions.ts
 * 2. Add an entry here with the lifecycle hooks you need
 */
const _AGENT_MODE_CHART: Record<AgentModeDefinition["type"], AgentModeNode> = {
  idling: {},
  working: {
    onEnter(agent, fromMode) {
      // A new turn starts from idling: forget the last turn's shapes.
      if (fromMode === "idling") {
        agent.createdShapes.clear();
      }
    },

    onExit(agent, _toMode) {
      // Unlock all shapes created during the prompt when exiting working mode
      agent.createdShapes.unlock();
    },

    onPromptCancel(agent, _request) {
      // Return to idling on cancel
      agent.mode.setMode("idling");
    },
  },
};

/**
 * Get the lifecycle node for a mode, if one exists.
 * This function helps TypeScript resolve types correctly with circular imports.
 */
export function getModeNode(mode: AgentModeType): AgentModeNode {
  return _AGENT_MODE_CHART[mode];
}
